import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { sectionFromHash } from './sections';

const STORE_KEY = 'c3-scroll';
const MAX_ENTRIES = 60;
/** A page that fills in late (the feed) may not be tall enough yet; keep trying for this long. */
const GIVE_UP_MS = 1500;

/** The scroll position of each history entry, by the router's `location.key`. Survives a reload. */
export class ScrollPositions {
  private positions = new Map<string, number>();

  constructor(private storage: Storage | null = safeSession()) {
    try {
      const saved = JSON.parse(storage?.getItem(STORE_KEY) ?? '{}') as Record<string, number>;
      for (const [key, y] of Object.entries(saved)) {
        if (typeof y === 'number') this.positions.set(key, y);
      }
    } catch {
      // A broken entry is the same as none.
    }
  }

  get(key: string): number | undefined {
    return this.positions.get(key);
  }

  set(key: string, y: number): void {
    this.positions.delete(key); // re-insert, so the oldest entry is the first one
    this.positions.set(key, Math.round(y));
    while (this.positions.size > MAX_ENTRIES) {
      this.positions.delete(this.positions.keys().next().value as string);
    }
  }

  flush(): void {
    try {
      this.storage?.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(this.positions)));
    } catch {
      // Storage can be blocked; positions then last until the page closes.
    }
  }
}

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Scrolls to `y`, retrying while the page grows, until it gets there, `GIVE_UP_MS` pass or the person scrolls. */
export function restoreScroll(y: number): () => void {
  let frame = 0;
  let stopped = false;
  const started = performance.now();
  const stop = () => {
    stopped = true;
    window.cancelAnimationFrame(frame);
    for (const type of ['wheel', 'touchstart', 'keydown'] as const) window.removeEventListener(type, stop);
  };
  const tick = () => {
    if (stopped) return;
    window.scrollTo(0, y);
    if (Math.abs(window.scrollY - y) <= 1 || performance.now() - started > GIVE_UP_MS) return stop();
    frame = window.requestAnimationFrame(tick);
  };
  for (const type of ['wheel', 'touchstart', 'keydown'] as const) {
    window.addEventListener(type, stop, { passive: true });
  }
  tick();
  return stop;
}

/**
 * Back and Forward return to where the page was scrolled, a reload keeps its place, and a view
 * opened from a link starts at the top (item 49). `BrowserRouter` has no `<ScrollRestoration>`.
 * Edits of the console's query string replace the entry and keep the scroll as it is.
 */
export function useScrollRestoration(): void {
  const { key, pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  const [store] = useState(() => new ScrollPositions());
  const currentKey = useRef(key);
  currentKey.current = key;
  const lastPath = useRef(pathname);

  useEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        store.set(currentKey.current, window.scrollY);
      });
    };
    const flush = () => store.flush();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', flush);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', flush);
      store.flush();
      window.history.scrollRestoration = previous;
    };
  }, [store]);

  useLayoutEffect(() => {
    const changedView = lastPath.current !== pathname;
    lastPath.current = pathname;
    if (navigationType === 'POP') {
      const saved = store.get(key);
      return saved === undefined ? undefined : restoreScroll(saved);
    }
    // A link to a section (`/#respond`) scrolls there itself; any other new view starts at the top.
    if (navigationType === 'PUSH' && changedView && !sectionFromHash(hash)) window.scrollTo(0, 0);
    return undefined;
  }, [store, key, pathname, hash, navigationType]);
}
